import { useCallback, useEffect, useRef, useState } from "react"
import styles from "@shared/styles/components/ScrollFadeContainer.module.css"
import { SystemIcon } from "./SystemIcon"
import { designSystemStyles } from "@shared/styles/designSystemStyles"

interface ScrollFadeContainerProps {
    children: React.ReactNode
    className?: string
}

const PAGE_SCROLL_RATIO = 0.8
const HOLD_START_DELAY_MS = 300
const HOLD_SCROLL_PX_PER_SECOND = 600

/**
 * Vertical scroll area that signals hidden content: the edges fade out over the
 * panel background and a small chevron invites scrolling. Each affordance only
 * shows while there is content hidden past its edge (top and/or bottom).
 * Tapping a chevron scrolls one page in its direction; pressing and holding
 * scrolls continuously until released.
 */
export const ScrollFadeContainer: React.FC<ScrollFadeContainerProps> = ({ children, className }) => {
    const scrollRef = useRef<HTMLDivElement>(null)
    const holdTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const holdAnimationRef = useRef<number | null>(null)
    const didHoldScrollRef = useRef(false)
    const [hasMoreAbove, setHasMoreAbove] = useState(false)
    const [hasMoreBelow, setHasMoreBelow] = useState(false)

    const updateAffordance = useCallback(() => {
        const element = scrollRef.current
        if (!element) return
        setHasMoreAbove(element.scrollTop > 4)
        setHasMoreBelow(element.scrollHeight - element.scrollTop - element.clientHeight > 4)
    }, [])

    const stopHoldScroll = useCallback(() => {
        if (holdTimeoutRef.current !== null) {
            clearTimeout(holdTimeoutRef.current)
            holdTimeoutRef.current = null
        }
        if (holdAnimationRef.current !== null) {
            cancelAnimationFrame(holdAnimationRef.current)
            holdAnimationRef.current = null
        }
    }, [])

    const startHoldScroll = useCallback((direction: 1 | -1) => {
        stopHoldScroll()
        didHoldScrollRef.current = false
        holdTimeoutRef.current = setTimeout(() => {
            holdTimeoutRef.current = null
            didHoldScrollRef.current = true
            let lastTimestamp: number | null = null
            const step = (timestamp: number) => {
                const element = scrollRef.current
                if (!element) return
                if (lastTimestamp !== null) {
                    const elapsedSeconds = (timestamp - lastTimestamp) / 1000
                    element.scrollTop += direction * HOLD_SCROLL_PX_PER_SECOND * elapsedSeconds
                }
                lastTimestamp = timestamp
                holdAnimationRef.current = requestAnimationFrame(step)
            }
            holdAnimationRef.current = requestAnimationFrame(step)
        }, HOLD_START_DELAY_MS)
    }, [stopHoldScroll])

    const scrollOnePage = useCallback((direction: 1 | -1) => {
        // A completed press-and-hold already scrolled; releasing it must not add a page jump.
        if (didHoldScrollRef.current) {
            didHoldScrollRef.current = false
            return
        }
        const element = scrollRef.current
        if (!element) return
        element.scrollBy({ top: direction * element.clientHeight * PAGE_SCROLL_RATIO, behavior: "smooth" })
    }, [])

    useEffect(() => {
        updateAffordance()
        const element = scrollRef.current
        if (!element || typeof ResizeObserver === "undefined") return

        const resizeObserver = new ResizeObserver(updateAffordance)
        resizeObserver.observe(element)
        const mutationObserver = new MutationObserver(updateAffordance)
        mutationObserver.observe(element, { childList: true, subtree: true })

        return () => {
            resizeObserver.disconnect()
            mutationObserver.disconnect()
        }
    }, [updateAffordance])

    useEffect(() => stopHoldScroll, [stopHoldScroll])

    const containerClassName = ["DSScrollFadeContainer", styles.container, className].filter(Boolean).join(" ")

    const renderChevronButton = (direction: 1 | -1) => (
        <button
            type="button"
            className={styles.chevronButton}
            aria-label={(direction === -1 ? "Desplazar hacia arriba" : "Desplazar hacia abajo").localized()}
            onClick={() => scrollOnePage(direction)}
            onPointerDown={() => startHoldScroll(direction)}
            onPointerUp={stopHoldScroll}
            onPointerLeave={stopHoldScroll}
            onPointerCancel={stopHoldScroll}
        >
            <SystemIcon
                type="chevron"
                color={designSystemStyles.colorTextSecondary}
                size="small"
                className={direction === -1 ? styles.chevronUp : styles.chevronDown}
            />
        </button>
    )

    return (
        <div className={containerClassName}>
            <div ref={scrollRef} className={styles.scrollArea} onScroll={updateAffordance}>
                {children}
            </div>
            {hasMoreAbove && (
                <div className={`${styles.fade} ${styles.fadeTop}`}>
                    {renderChevronButton(-1)}
                </div>
            )}
            {hasMoreBelow && (
                <div className={`${styles.fade} ${styles.fadeBottom}`}>
                    {renderChevronButton(1)}
                </div>
            )}
        </div>
    )
}
